import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { api } from "./api";

export type Role = "admin" | "operator" | "viewer";

export interface AuthUser {
	id: string;
	username: string;
	role: Role;
}

interface AuthContextValue {
	user: AuthUser | null;
	isLoading: boolean;
	refresh: () => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient();
	const meQuery = useQuery({
		queryKey: ["auth", "me"],
		queryFn: async () => {
			const { data } = await api.api.auth.me.get();
			if (!data || data.success !== true) return null;
			return data.user as AuthUser;
		},
		retry: false,
		staleTime: 30_000,
	});

	const value = useMemo<AuthContextValue>(
		() => ({
			user: meQuery.data ?? null,
			isLoading: meQuery.isLoading,
			refresh: async () => {
				await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
			},
			logout: async () => {
				try {
					await api.api.auth.logout.post();
				} catch {
					/* ignore — clear state regardless of server reachability */
				}
				// `clear()` alone leaves `data` as the previous user while /me refetches,
				// so App never switches to Login. Force anonymous state, then drop the
				// rest of the cache so we don't flash stale dashboard data after sign-in.
				await queryClient.cancelQueries();
				queryClient.setQueryData(["auth", "me"], null);
				queryClient.removeQueries({
					predicate: (query) =>
						query.queryKey[0] !== "auth" || query.queryKey[1] !== "me",
				});
			},
		}),
		[meQuery.data, meQuery.isLoading, queryClient],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
}

export function hasRole(user: AuthUser | null, minimum: Role): boolean {
	if (!user) return false;
	const rank = { viewer: 0, operator: 1, admin: 2 } as const;
	return rank[user.role] >= rank[minimum];
}
